import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import type {ContentRelationshipField, KeyTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import Link from 'next/link'
import {resolveParentBasePath} from 'prismicio'
import type {BlogPostDocument} from 'prismicio-types'
import type {FC} from 'react'
import {memo} from 'react'
import {useGetBlogPostCategory} from 'src/common/hooks/useGetBlogPostCategory'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {formatDate} from '../utils'

type BlogPostCardProps = {
  post: BlogPostDocument<string>
  index: number
  pageUid: string
  buttonLabel: KeyTextField
}

export const BlogPostCard: FC<BlogPostCardProps> = memo(function ({post, index, pageUid, buttonLabel}) {
  const domainCountry = useGetDomainCountry()
  const postLink = createPostLink(post.data.parent_page, post.uid)
  const category = useGetBlogPostCategory(post.data.category)

  const formattedDate = formatDate(post.data.date)

  return (
    <div className="group/card relative box-border flex size-full flex-col gap-6 rounded-[8px] border border-solid border-black/10 bg-moss-white p-6">
      {postLink && (
        <Link
          href={postLink}
          locale={post.lang}
          className="absolute inset-0 z-10 rounded-[8px] focus-visible:ring-2 focus-visible:ring-green-neon focus-visible:ring-offset-2"
          aria-label={post.data.title ?? ''}
          onClick={() => {
            trackMossAnalytics({
              event: 'interaction',
              interaction: {
                element: 'blog-post-card',
                elementText: post.data.title ?? '',
                location: `blog-posts-container-post-${index + 1}`,
                type: 'click',
              },
              page: {domainCountry, type: pageUid || 'unknown'},
            })
          }}
        />
      )}
      {/* Image */}
      <PrismicNextImage field={post.data.meta_image} className="aspect-[168/97] rounded-[4px]" />

      {/* Content */}
      <div className="flex min-h-0 w-full flex-1 flex-col items-start gap-4 md:gap-6">
        <div className="flex min-h-0 w-full flex-1 flex-col items-start justify-between gap-4 md:gap-6">
          <div className="flex w-full flex-col gap-4 md:gap-6">
            {/* Header - Tag and Date */}
            <div className="flex items-center gap-4">
              {category && (
                <div className="box-border flex h-[30px] items-center gap-1 rounded-4 bg-moss-blue-light p-2 max-md:px-2.5 md:h-auto md:py-[5px]">
                  <p className="text-detail text-moss-blue">{category}</p>
                </div>
              )}
              {formattedDate && (
                <div className="flex flex-col justify-center leading-[0]">
                  <p className="text-detail text-moss-grey-400">{formattedDate}</p>
                </div>
              )}
            </div>

            {/* Text - Title and Description */}
            <div className="flex w-full flex-col items-start gap-4">
              <h4 className="w-full overflow-hidden text-ellipsis text-24 font-medium leading-[30px] text-moss-black md:text-18 md:leading-6">
                {post.data.title}
              </h4>
              {post.data.description && (
                <p className="w-full text-15 font-normal leading-5 text-moss-grey-550">{post.data.description}</p>
              )}
            </div>
          </div>

          {/* Button */}
          {postLink && (
            <Link
              href={postLink}
              locale={post.lang}
              className="button-secondary button-light flex items-center self-end border border-solid border-black/10 transition-colors duration-100 group-hover/card:bg-moss-grey-100"
              onClick={() => {
                trackMossAnalytics({
                  event: 'interaction',
                  interaction: {
                    element: `read-blog-post-button`,
                    elementText: buttonLabel || 'Read more',
                    location: `blog-posts-container-post-${index + 1}`,
                    type: 'click',
                  },
                  page: {
                    domainCountry,
                    type: pageUid || 'unknown',
                  },
                })
              }}
            >
              <p className="text-button-s uppercase text-moss-black">{buttonLabel || 'Read more'}</p>
              <div className="flex size-8 items-center justify-center text-clip rounded-sm bg-green-neon md:size-6">
                <p className="text-center text-12 font-semibold leading-normal tracking-[-0.12px] text-green-dark">→</p>
              </div>
            </Link>
          )}
        </div>
      </div>
    </div>
  )
})

const createPostLink = (parentPage: ContentRelationshipField, postUid: KeyTextField) => {
  if (!isFilled.contentRelationship(parentPage) || !isFilled.keyText(postUid)) {
    return null
  }

  const basePath = resolveParentBasePath(parentPage)

  return basePath ? `${basePath}/${postUid}` : null
}
